import sanitizeHtml from 'sanitize-html'

/** Allowed mustache tokens in the welcome email body. */
export const WELCOME_EMAIL_ALLOWED_VARS = [
  'fullName',
  'firstName',
  'button',
  'location',
  'link',
  'email',
]

/** Variables admins can insert from the Welcome email UI. */
export const WELCOME_EMAIL_INSERT_VARS = [
  { token: '{{ fullName }}', label: '{{ fullName }}' },
  { token: '{{ firstName }}', label: '{{ firstName }}' },
  { token: '{{ location }}', label: '{{ location }}' },
  { token: '{{ button }}', label: '{{ button }}' },
]

const WELCOME_EMAIL_USER_FACING_VARS = WELCOME_EMAIL_INSERT_VARS.map(
  (v) => v.token,
).join(', ')

/** Count visible characters (Unicode code points) — emoji count as 1, not 2. */
export const visibleCharCount = (value) =>
  Array.from(String(value || '')).length

/** Truncate a string to at most `max` visible characters. */
export const truncateVisibleChars = (value, max) => {
  const chars = Array.from(String(value || ''))
  if (chars.length <= max) return String(value || '')
  return chars.slice(0, max).join('')
}

/** Plain text from HTML for length / emptiness checks. */
export const plainTextFromHtml = (html) =>
  sanitizeHtml(String(html || ''), {
    allowedTags: [],
    allowedAttributes: {},
  })
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

export const plainTextLength = (html) =>
  visibleCharCount(
    sanitizeHtml(String(html || ''), {
      allowedTags: [],
      allowedAttributes: {},
    }).replace(/\u00a0/g, ' '),
  )

export const hasNonEmptyText = (value) =>
  typeof value === 'string' && value.trim().length > 0

export const hasNonEmptyHtml = (html) => plainTextFromHtml(html).length > 0

/** True when the body still has the enroll CTA (`{{ button }}` or rendered link). */
export const hasWelcomeEmailButton = (html) => {
  const raw = String(html || '')
  if (/\{\{\s*button(?:\s*\|\s*safe)?\s*\}\}/i.test(raw)) return true
  if (/href\s*=\s*["']\s*\{\{\s*link\s*\}\}\s*["']/i.test(raw)) return true
  return false
}

/** Extract `{{ token }}` names from HTML / text. */
export const extractTemplateVars = (html) => {
  const found = []
  const re = /\{\{\s*([a-zA-Z0-9_]+)(?:\s*\|\s*[a-zA-Z0-9_]+)?\s*\}\}/g
  const raw = String(html || '')
  let match
  while ((match = re.exec(raw))) {
    found.push(match[1])
  }
  return found
}

export const findUnknownTemplateVars = (
  html,
  allowed = WELCOME_EMAIL_ALLOWED_VARS,
) => {
  const allowedSet = new Set(allowed.map((v) => v.toLowerCase()))
  const unknown = new Set()
  extractTemplateVars(html).forEach((name) => {
    if (!allowedSet.has(name.toLowerCase())) {
      unknown.add(name)
    }
  })
  return Array.from(unknown)
}

/**
 * Strip unsafe URL schemes from rich-text HTML while keeping https/mailto/relative.
 */
export const sanitizeEmailHtmlLinks = (html) =>
  sanitizeHtml(String(html || ''), {
    allowedTags: sanitizeHtml.defaults.allowedTags.concat([
      'img',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'span',
      'div',
      'figure',
      'figcaption',
      'u',
      's',
      'br',
      'hr',
    ]),
    allowedAttributes: {
      ...sanitizeHtml.defaults.allowedAttributes,
      a: ['href', 'name', 'target', 'rel', 'class'],
      img: ['src', 'alt', 'title', 'width', 'height', 'class'],
      '*': ['class', 'style'],
    },
    allowedSchemes: ['http', 'https', 'mailto'],
    allowProtocolRelative: false,
    transformTags: {
      a: (tagName, attribs) => {
        const href = attribs.href || ''
        if (/^\s*javascript:/i.test(href) || /^\s*data:/i.test(href)) {
          const next = { ...attribs }
          delete next.href
          return { tagName, attribs: next }
        }
        return { tagName, attribs }
      },
    },
  })

/**
 * Validate welcome email editable body before save.
 * @returns {{ ok: boolean, error?: string, completenessHtml: string }}
 */
export const validateWelcomeEmailBody = (html) => {
  if (!hasNonEmptyHtml(html)) {
    return {
      ok: false,
      error: 'Email body is required.',
      completenessHtml: '',
    }
  }
  if (!hasWelcomeEmailButton(html)) {
    return {
      ok: false,
      error: "{{ button }} is required. It's the signup link.",
      completenessHtml: '',
    }
  }
  const unknown = findUnknownTemplateVars(html)
  if (unknown.length > 0) {
    return {
      ok: false,
      error: `Unknown variable {{ ${unknown[0]} }}. Use only ${WELCOME_EMAIL_USER_FACING_VARS}.`,
      completenessHtml: '',
    }
  }
  return { ok: true, completenessHtml: html }
}

export const SIGNUP_LIMITS = {
  title: 40,
  header: 40,
  body: 450,
  welcomeLetter: 4000,
}

/**
 * Validate signup / invite-link fields (limits only — empty is allowed so
 * blur autosave still runs).
 * @returns {Object} Formik-style errors map (empty when valid)
 */
export const validateSignupPageValues = (values) => {
  const errors = {}
  const title = values?.title || ''
  const header = values?.header || ''
  const body = values?.body || ''
  const welcomeLetter = values?.welcomeLetter || ''

  if (visibleCharCount(title) > SIGNUP_LIMITS.title) {
    errors.title = `Maximum ${SIGNUP_LIMITS.title} characters.`
  }

  if (visibleCharCount(header) > SIGNUP_LIMITS.header) {
    errors.header = `Maximum ${SIGNUP_LIMITS.header} characters.`
  }

  if (plainTextLength(welcomeLetter) > SIGNUP_LIMITS.welcomeLetter) {
    errors.welcomeLetter = `Maximum ${SIGNUP_LIMITS.welcomeLetter} characters.`
  }

  if (plainTextLength(body) > SIGNUP_LIMITS.body) {
    errors.body = `Maximum ${SIGNUP_LIMITS.body} characters.`
  }

  return errors
}

/** True when signup page has any of the four fields filled (rail scoring). */
export const isSignupPageComplete = (values) => {
  const v = values || {}
  return (
    hasNonEmptyText(v.title) ||
    hasNonEmptyText(v.header) ||
    hasNonEmptyHtml(v.body) ||
    hasNonEmptyHtml(v.welcomeLetter) ||
    hasNonEmptyText(v.signupTitle) ||
    hasNonEmptyText(v.signupHeader) ||
    hasNonEmptyHtml(v.signupBody)
  )
}

/** True when welcome email body is complete and has {{ button }}. */
export const isWelcomeEmailComplete = (html) =>
  validateWelcomeEmailBody(html).ok
